import React, { useEffect, useMemo, useState } from 'react';
import { Layout, Form, Input, Button, message, Typography, Flex, Modal } from 'antd';
import { LockOutlined, KeyOutlined, UserOutlined } from '@ant-design/icons';
import type { RuleObject } from 'antd/es/form';
import type { StoreValue } from 'antd/es/form/interface';
import { observer } from 'mobx-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../../store/useStore';
import { resolveRequestRegion, saveUserRegion } from '../../../utils/region';
import { resolveAccount } from '../../../utils/identity';
import useCountdown from '../../../hooks/useCountDown';
import { useTranslation } from '../../../i18n';
import LanguageSwitcher from '../../../components/LanguageSwitcher';
import { HomeLink } from '../../../components/HomeLink';
import type { WechatQrLogin } from '../../../types/wechat';
import { ResetPasswordModal } from './ResetPasswordModal';

type AuthMode = 'login' | 'register';
type LoginTab = 'code' | 'password';

type LoginFormValues = {
  account?: string;
  verifyCode?: string;
  password?: string;
};

type RegisterFormValues = {
  displayName?: string;
  account?: string;
  verifyCode?: string;
  password?: string;
};

function expiresAtMs(value: WechatQrLogin['expiresAt']) {
  if (!value) return null;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (typeof value.seconds !== 'number' || !Number.isFinite(value.seconds)) return null;
  const nanos = typeof value.nanos === 'number' && Number.isFinite(value.nanos) ? value.nanos : 0;
  return value.seconds * 1000 + Math.floor(Math.min(Math.max(nanos, 0), 999999999) / 1e6);
}

function remainText(ms: number) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

const { Text, Title } = Typography;

export const Login = observer(() => {
  const { t } = useTranslation();
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [loginTab, setLoginTab] = useState<LoginTab>('code');
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginStore } = useStore();
  const { countdown, isRunning, startCountdown, resetCountdown } = useCountdown();
  const [wechatOpen, setWechatOpen] = useState(false);
  const [wechatLoading, setWechatLoading] = useState(false);
  const [wechatQr, setWechatQr] = useState<WechatQrLogin | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    loginStore.logout();
  }, [loginStore]);

  useEffect(() => {
    if (!wechatOpen) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [wechatOpen]);

  const accountFieldRules = useMemo(
    () => [
      {
        validator: (_rule: RuleObject, value: StoreValue) => {
          const text = typeof value === 'string' ? value.trim() : '';
          if (!text) return Promise.reject(new Error(t('login.account_required')));
          if (resolveAccount(text)) return Promise.resolve();
          return Promise.reject(new Error(t('login.account_invalid')));
        }
      }
    ],
    [t]
  );

  // 切换登录/注册
  const switchAuthMode = (mode: AuthMode) => {
    if (mode === authMode) return;
    setAuthMode(mode);
    form.resetFields();
    resetCountdown();
  };

  const handleLoginTabChange = (tab: LoginTab) => {
    if (tab === loginTab) return;
    setLoginTab(tab);
    form.resetFields();
    resetCountdown();
  };

  const sendVerifyCode = async () => {
    if (isRunning) return;
    const values = await form.validateFields(['account']).catch(() => null);
    const credential = resolveAccount(String(values?.account ?? ''));
    if (!credential) return;
    const identity = 'phone' in credential ? credential.phone : credential.email;
    try {
      const res = await loginStore.sendVerificationCode(
        identity,
        authMode === 'register' ? 'REGISTER' : 'LOGIN'
      );
      if (res.code === 200) {
        message.success(t('login.code_sent'));
        startCountdown();
      } else {
        message.error(res.message || t('login.send_failed'));
      }
    } catch {
      message.error(t('login.send_failed_retry'));
    }
  };

  const accountField = (
    <Form.Item
      name='account'
      label={<Text className='login-panel-field-label'>{t('login.account_label')}</Text>}
      rules={accountFieldRules}
    >
      <Input
        prefix={<UserOutlined className='site-form-item-icon' />}
        placeholder={t('login.account_placeholder')}
        size='large'
        maxLength={320}
      />
    </Form.Item>
  );

  const onLoginSubmit = async (values: LoginFormValues) => {
    const credential = resolveAccount(values.account?.trim() ?? '');
    if (!credential) return;
    const identity = 'phone' in credential ? credential.phone : credential.email;
    try {
      const res =
        loginTab === 'code'
          ? await loginStore.loginByCode({ identity, code: values.verifyCode ?? '' })
          : await loginStore.loginByPassword({ identity, password: values.password ?? '' });
      if (res.code !== 200) {
        message.error(res.message || t('login.login_failed'));
        return;
      }
      const session = loginStore.applyLoginContent(res.content ?? res.data, credential);
      if (!session) {
        message.error(res.message || t('login.login_failed'));
        return;
      }
      if (session.region) saveUserRegion(session.region);
      message.success(t('login.login_success'));
      const redirectTo =
        (location.state as { from?: { pathname?: string } })?.from?.pathname ?? '/creator';
      navigate(redirectTo, { replace: true });
    } catch {
      message.error(t('login.login_failed_retry'));
    }
  };

  const onRegisterSubmit = async (values: RegisterFormValues) => {
    const credential = resolveAccount(values.account?.trim() ?? '');
    if (!credential) return;
    const identity = 'phone' in credential ? credential.phone : credential.email;
    try {
      const res = await loginStore.register({
        identity,
        code: values.verifyCode ?? '',
        password: values.password ?? '',
        displayName: values.displayName?.trim() ?? ''
      });
      if (res.code === 200) {
        saveUserRegion(resolveRequestRegion());
        message.success(t('login.register_success'));
        switchAuthMode('login');
      } else {
        message.error(res.message || t('login.register_failed'));
      }
    } catch {
      message.error(t('login.register_failed_retry'));
    }
  };

  const closeWechat = () => {
    setWechatOpen(false);
    setWechatQr(null);
  };

  const openWechatLogin = async () => {
    if (wechatLoading) return;
    setWechatLoading(true);
    try {
      const res = await loginStore.createWechatQrLogin();
      const qrCodeUrl = res.content?.qrCodeUrl?.trim() ?? '';
      if (res.code !== 200 || !qrCodeUrl) {
        if (res.code !== -1) message.error(res.message || t('login.wechat_qr_failed'));
        return;
      }
      setNow(Date.now());
      setWechatQr({
        sceneId: res.content?.sceneId,
        loginKey: res.content?.loginKey,
        qrCodeUrl,
        expiresAt: res.content?.expiresAt
      });
      setWechatOpen(true);
    } catch {
      message.error(t('login.wechat_qr_failed'));
    } finally {
      setWechatLoading(false);
    }
  };

  const expireMs = expiresAtMs(wechatQr?.expiresAt);
  const wechatExpired = expireMs !== null && now >= expireMs;
  const isRegister = authMode === 'register';

  return (
    <Layout className='login-layout'>
      <div className='login-bg' aria-hidden />
      <HomeLink className='auth-home-link auth-home-link--dark' />
      <div className='login-lang-switcher'>
        <LanguageSwitcher />
      </div>
      <div className='login-left'>
        <div className='login-left-brand'>
          <div className='login-left-brand-text'>
            <p className='login-left-product'>
              <img
                src={`${process.env.PUBLIC_URL || ''}/Wowbit-logo.png`}
                alt='Wowbit'
                className='login-left-wordmark'
              />
              <span className='login-left-product-name'> · {t('login.brand_product')}</span>
            </p>
            <p className='login-left-tagline'>{t('login.brand_tagline')}</p>
          </div>
        </div>
        <div className='login-left-hero'>
          <h1 className='login-left-welcome'>{t('login.hero_welcome')}</h1>
          <p className='login-left-desc'>{t('login.hero_desc')}</p>
        </div>
        <div className='login-left-feature'>
          <p className='login-left-feature-text'>
            {t('login.hero_feature')}
            <span className='login-left-feature-highlight'>{t('login.hero_feature_highlight')}</span>
          </p>
        </div>
      </div>
      <div className='login'>
        <div className='login-panel'>
          <header className='login-panel-header'>
            <Title level={3} className='login-panel-title' style={{ fontSize: '28px !important' }}>
              {isRegister ? t('login.register_title') : t('login.title')}
            </Title>
            <p className='login-panel-subtitle'>
              {isRegister ? t('login.register_subtitle') : t('login.subtitle')}
            </p>
          </header>

          {!isRegister ? (
            <>
              <div className='wechat-btn'>
                <Button
                  type='primary'
                  size='large'
                  className='wechat-btn-icon'
                  loading={wechatLoading}
                  onClick={openWechatLogin}
                >
                  {t('login.wechat_login')}
                </Button>
              </div>
              <div className='login-panel-divider' role='separator' aria-label={t('login.or')}>
                <span className='login-panel-divider-line' />
                <span className='login-panel-divider-text'>{t('login.or')}</span>
                <span className='login-panel-divider-line' />
              </div>
              <div className='login-panel-tabs' role='tablist' aria-label={t('login.login_method')}>
                <button
                  id='login-tab-code'
                  type='button'
                  role='tab'
                  aria-selected={loginTab === 'code'}
                  aria-controls='login-panel-form'
                  className={`login-panel-tab${loginTab === 'code' ? ' is-active' : ''}`}
                  onClick={() => handleLoginTabChange('code')}
                >
                  {t('login.code_login')}
                </button>
                <button
                  id='login-tab-password'
                  type='button'
                  role='tab'
                  aria-selected={loginTab === 'password'}
                  aria-controls='login-panel-form'
                  className={`login-panel-tab${loginTab === 'password' ? ' is-active' : ''}`}
                  onClick={() => handleLoginTabChange('password')}
                >
                  {t('login.password_login')}
                </button>
              </div>
            </>
          ) : null}

          {isRegister ? (
            <Form<RegisterFormValues>
              form={form}
              layout='vertical'
              name='register_form'
              requiredMark={false}
              style={{ width: '100%' }}
              preserve={false}
              onFinish={onRegisterSubmit}
            >
              <div id='login-panel-form' className='login-panel-form-body'>
                <Form.Item
                  name='displayName'
                  label={<Text className='login-panel-field-label'>{t('login.username_label')}</Text>}
                  rules={[
                    { required: true, whitespace: true, message: t('login.username_required') },
                    { max: 12, message: t('login.display_name_max') }
                  ]}
                >
                  <Input
                    prefix={<UserOutlined className='site-form-item-icon' />}
                    placeholder={t('login.username_placeholder')}
                    size='large'
                    maxLength={12}
                    showCount
                  />
                </Form.Item>
                {accountField}
                <Form.Item label={<Text className='login-panel-field-label'>{t('login.code_label')}</Text>}>
                  <Flex gap={8} align='start'>
                    <Form.Item
                      name='verifyCode'
                      className='login-panel-verify-item'
                      rules={[
                        { required: true, message: t('login.code_required') },
                        { pattern: /^[0-9]{6}$/, message: t('login.code_digits') }
                      ]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input
                        prefix={<KeyOutlined className='site-form-item-icon' />}
                        placeholder={t('login.code_placeholder')}
                        size='large'
                        maxLength={6}
                      />
                    </Form.Item>
                    <Button
                      size='large'
                      className='login-panel-send-code'
                      disabled={isRunning}
                      onClick={sendVerifyCode}
                    >
                      {isRunning ? `${countdown}s` : t('login.send_code')}
                    </Button>
                  </Flex>
                </Form.Item>
                <Form.Item
                  name='password'
                  label={<Text className='login-panel-field-label'>{t('login.password_label')}</Text>}
                  rules={[
                    { required: true, message: t('login.password_required') },
                    {
                      validator: (_, value: string) => {
                        const password = value ?? '';
                        if (!password) return Promise.resolve();
                        const bytes = new TextEncoder().encode(password).length;
                        if (password.length >= 8 && password.length <= 24 && bytes <= 72) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('login.password_rule')));
                      }
                    }
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined className='site-form-item-icon' />}
                    placeholder={t('login.password_rule')}
                    size='large'
                    maxLength={72}
                  />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0, marginTop: 4 }}>
                  <Button
                    type='primary'
                    htmlType='submit'
                    className='login-panel-submit'
                    block
                    size='large'
                  >
                    {t('login.register_btn')}
                  </Button>
                </Form.Item>
              </div>
            </Form>
          ) : (
            <Form<LoginFormValues>
              form={form}
              layout='vertical'
              name='normal_login'
              requiredMark={false}
              style={{ width: '100%' }}
              preserve={false}
              onFinish={onLoginSubmit}
            >
              <div
                id='login-panel-form'
                key={loginTab}
                className='login-panel-form-body'
                role='tabpanel'
                aria-labelledby={
                  loginTab === 'code' ? 'login-tab-code' : 'login-tab-password'
                }
              >
                {loginTab === 'code' ? (
                  <>
                    {accountField}
                    <Form.Item
                      label={<Text className='login-panel-field-label'>{t('login.code_label')}</Text>}
                    >
                      <Flex gap={8} align='start'>
                        <Form.Item
                          name='verifyCode'
                          className='login-panel-verify-item'
                          style={{ flex: 1, marginBottom: 0 }}
                          rules={[
                            { required: true, message: t('login.code_required') },
                            { pattern: /^[0-9]{6}$/, message: t('login.code_digits') }
                          ]}
                        >
                          <Input
                            prefix={<KeyOutlined className='site-form-item-icon' />}
                            placeholder={t('login.code_placeholder')}
                            size='large'
                            maxLength={6}
                          />
                        </Form.Item>
                        <Button
                          size='large'
                          className='login-panel-send-code'
                          disabled={isRunning}
                          onClick={sendVerifyCode}
                        >
                          {isRunning ? `${countdown}s` : t('login.send_code')}
                        </Button>
                      </Flex>
                    </Form.Item>
                  </>
                ) : (
                  <>
                    {accountField}
                    <Form.Item
                      name='password'
                      label={<Text className='login-panel-field-label'>{t('login.password_label')}</Text>}
                      rules={[
                        { required: true, message: t('login.password_required') },
                        {
                          validator: (_, value: string) => {
                            const password = value ?? '';
                            if (!password) return Promise.resolve();
                            const bytes = new TextEncoder().encode(password).length;
                            if (password.length <= 72 && bytes <= 72) return Promise.resolve();
                            return Promise.reject(new Error(t('login.login_password_rule')));
                          }
                        }
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined className='site-form-item-icon' />}
                        placeholder={t('login.password_placeholder')}
                        size='large'
                        maxLength={72}
                      />
                    </Form.Item>
                  </>
                )}
                <Form.Item style={{ marginBottom: 0, marginTop: 4 }}>
                  <Button
                    type='primary'
                    htmlType='submit'
                    className='login-panel-submit'
                    block
                    size='large'
                  >
                    {t('login.login_btn')}
                  </Button>
                </Form.Item>
              </div>
            </Form>
          )}

          <div className='login-panel-footer'>
            {isRegister ? (
              <>
                <span />
                <button
                  type='button'
                  className='login-panel-footer-link login-panel-footer-link--accent'
                  onClick={() => switchAuthMode('login')}
                >
                  {t('login.go_login')}
                </button>
              </>
            ) : (
              <>
                <button
                  type='button'
                  className='login-panel-footer-link'
                  onClick={() => setResetOpen(true)}
                >
                  {t('login.forgot_password')}
                </button>
                <button
                  type='button'
                  className='login-panel-footer-link login-panel-footer-link--accent'
                  onClick={() => switchAuthMode('register')}
                >
                  {t('login.go_register')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <ResetPasswordModal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        onSuccess={() => {
          setLoginTab('password');
          switchAuthMode('login');
        }}
      />
      <Modal
        open={wechatOpen}
        title={t('login.wechat_qr_title')}
        footer={null}
        centered
        width={360}
        destroyOnClose
        onCancel={closeWechat}
      >
        <div className='wechat-qr-modal'>
          {wechatQr?.qrCodeUrl ? (
            <>
              <div className={`wechat-qr-frame${wechatExpired ? ' is-expired' : ''}`}>
                <img className='wechat-qr-image' src={wechatQr.qrCodeUrl} alt={t('login.wechat_scan')} />
                {wechatExpired ? (
                  <div className='wechat-qr-expired'>
                    <span>{t('login.wechat_expired')}</span>
                    <Button type='primary' loading={wechatLoading} onClick={openWechatLogin}>
                      {t('login.wechat_refresh')}
                    </Button>
                  </div>
                ) : null}
              </div>
              <p className='wechat-qr-hint'>{t('login.wechat_scan')}</p>
              {expireMs !== null && !wechatExpired ? (
                <p className='wechat-qr-expire'>
                  {t('login.wechat_expires_in', { time: remainText(expireMs - now) })}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      </Modal>
    </Layout>
  );
});
